/**
 * CompanyDocsContent - Content renderer for Company Documents panel
 * 
 * Displays policies and templates with tabs.
 * Clicking a document opens it in the document viewer.
 */

import { useState, useEffect } from 'react';
import type { CompanyDocument } from '../../types';
import { Button } from '@/components/ui';

type DocumentTab = 'policies' | 'templates';

// Key for storing recent documents in localStorage (shared across Aerotage)
const RECENT_DOCS_KEY = 'aeroRecentDocuments';

interface CompanyDocsContentProps {
  documents: CompanyDocument[];
  loading: boolean;
  isSearching: boolean;
  searchQuery: string;
  onDocumentClick: (doc: CompanyDocument) => void;
}

export function CompanyDocsContent({
  documents,
  loading,
  isSearching,
  searchQuery,
  onDocumentClick,
}: CompanyDocsContentProps) {
  const [activeTab, setActiveTab] = useState<DocumentTab>('policies');
  const [recentDocIds, setRecentDocIds] = useState<string[]>([]);
  
  // Load and sync recent documents from localStorage
  useEffect(() => {
    const loadRecentDocs = () => {
      try {
        const stored = localStorage.getItem(RECENT_DOCS_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            // Recent docs are stored as objects with id property
            // Filter for company documents only (those with company- prefix or type 'company')
            interface RecentDocItem {
              id?: string;
              type?: string;
            }
            const companyDocIds = parsed
              .filter((item: string | RecentDocItem) => {
                if (typeof item === 'string') return item.startsWith('company-');
                if (item && typeof item === 'object') {
                  return item.id?.startsWith('company-') || item.type === 'company';
                }
                return false;
              })
              .map((item: string | RecentDocItem) => typeof item === 'string' ? item : item.id);
            setRecentDocIds(companyDocIds);
          }
        }
      } catch {
        // Ignore parse errors
      }
    };
    
    loadRecentDocs();
    
    // Poll for updates (in case another tab updates)
    const interval = setInterval(loadRecentDocs, 2000);
    
    // Listen for storage events from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === RECENT_DOCS_KEY) {
        loadRecentDocs();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // Get recent documents by matching IDs to documents list
  const recentDocuments = recentDocIds
    .map(recentId => {
      // Strip the company- prefix to match documentId
      const docId = recentId.replace(/^company-/, '');
      return documents.find(d => d.documentId === docId);
    })
    .filter((d): d is CompanyDocument => d !== undefined)
    .slice(0, 5); // Show max 5 recent
  
  // Loading state
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3 mx-auto"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Searching state
  if (isSearching) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin text-3xl mb-3">🔍</div>
        <p className="text-muted-foreground">Searching documents...</p>
      </div>
    );
  }
  
  // Split documents by type
  const policies = documents.filter(d => d.documentType === 'policy');
  const templates = documents.filter(d => d.documentType === 'template');
  
  // Filter by search query
  const filterDocs = (docs: CompanyDocument[]) => {
    if (!searchQuery.trim()) return docs;
    const query = searchQuery.toLowerCase();
    return docs.filter(d => 
      d.documentId.toLowerCase().includes(query) ||
      d.title.toLowerCase().includes(query) ||
      d.category?.toLowerCase().includes(query)
    );
  };
  
  const filteredPolicies = filterDocs(policies);
  const filteredTemplates = filterDocs(templates);
  
  const currentDocs = activeTab === 'policies' ? filteredPolicies : filteredTemplates;
  
  // Empty state
  if (documents.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-5xl mb-4">🏢</div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          No company documents yet
        </h3>
        <p className="text-muted-foreground">
          Company policies and templates will appear here
        </p>
      </div>
    );
  }
  
  // Group documents by category
  const groupByCategory = (docs: CompanyDocument[]) => {
    const groups: Record<string, CompanyDocument[]> = {};
    docs.forEach(doc => {
      const category = doc.category || 'Uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(doc);
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  };
  
  const groupedDocs = groupByCategory(currentDocs);
  
  return (
    <div className="flex flex-col h-full">
      {/* Recently Opened Section - Above tabs */}
      {recentDocuments.length > 0 && !searchQuery.trim() && (
        <div className="flex-shrink-0 p-4">
          <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
            🕐 Recently Opened
          </h3>
          <div className="flex flex-wrap gap-2">
            {recentDocuments.map(doc => (
              <Button
                key={doc.documentId}
                onClick={() => onDocumentClick(doc)}
                variant="ghost"
                size="sm"
                className="flex items-center gap-1.5 bg-muted/30 hover:bg-muted/50"
              >
                <span>{doc.documentType === 'policy' ? '📜' : '📄'}</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {doc.documentId}
                </span>
                <span className="text-foreground truncate max-w-[150px]">
                  {doc.title}
                </span>
              </Button>
            ))}
          </div>
        </div>
      )}
      
      {/* Tabs */}
      <div className="flex-shrink-0 px-4">
        <div className="flex gap-4">
          <Button
            onClick={() => setActiveTab('policies')}
            variant="ghost"
            className={`py-2 px-1 text-sm font-medium border-b-2 rounded-none h-auto ${
              activeTab === 'policies'
                ? 'border-primary text-info'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            📜 Policies ({filteredPolicies.length})
          </Button>
          <Button
            onClick={() => setActiveTab('templates')}
            variant="ghost"
            className={`py-2 px-1 text-sm font-medium border-b-2 rounded-none h-auto ${
              activeTab === 'templates'
                ? 'border-primary text-info'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            📄 Templates ({filteredTemplates.length})
          </Button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {searchQuery.trim() && (
          <div className="text-sm text-muted-foreground">
            Found {currentDocs.length} {activeTab === 'policies' ? 'polic' : 'template'}{currentDocs.length !== 1 ? (activeTab === 'policies' ? 'ies' : 's') : (activeTab === 'policies' ? 'y' : '')}
          </div>
        )}
        
        {currentDocs.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-3">📭</div>
            <p className="text-muted-foreground">
              {searchQuery.trim() 
                ? `No ${activeTab} match "${searchQuery}"`
                : `No ${activeTab} available`
              }
            </p>
          </div>
        ) : (
          groupedDocs.map(([category, docs]) => (
            <DocumentCategoryGroup
              key={category}
              category={category}
              documents={docs}
              onDocumentClick={onDocumentClick}
            />
          ))
        )}
      </div>
    </div>
  );
}

// Category group component
interface DocumentCategoryGroupProps {
  category: string;
  documents: CompanyDocument[];
  onDocumentClick: (doc: CompanyDocument) => void;
}

function DocumentCategoryGroup({
  category,
  documents,
  onDocumentClick,
}: DocumentCategoryGroupProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Format category name
  const formatCategory = (cat: string) => {
    return cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Group header */}
      <Button
        onClick={() => setIsExpanded(!isExpanded)}
        variant="ghost"
        className="w-full flex items-center justify-between px-4 py-2 bg-muted/30 hover:bg-muted/50 h-auto rounded-none"
      >
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground">
            {formatCategory(category)}
          </span>
          <span className="text-sm text-muted-foreground">
            ({documents.length})
          </span>
        </div>
        <span className="text-muted-foreground">
          {isExpanded ? '▼' : '▶'}
        </span>
      </Button>
      
      {/* Documents */}
      {isExpanded && (
        <div className="divide-y divide-gray-100 dark:divide-slate-700">
          {documents.map(doc => (
            <CompanyDocCard
              key={doc.documentId}
              document={doc}
              onClick={() => onDocumentClick(doc)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Document card component
interface CompanyDocCardProps {
  document: CompanyDocument;
  onClick: () => void;
}

function CompanyDocCard({ document, onClick }: CompanyDocCardProps) {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Released':
        return 'bg-success-light text-success';
      case 'Approved':
        return 'bg-info-light text-info';
      case 'In Review':
        return 'bg-warning-light text-warning';
      case 'Draft':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };
  
  return (
    <div
      className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={onClick}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-muted-foreground">
              {document.documentId}
            </span>
            <span className="text-xs text-muted-foreground">
              Rev {document.currentRevision}
            </span>
          </div>
          <h4 className="font-medium text-foreground mt-0.5">
            {document.title}
          </h4>
        </div>
        
        {/* Status badge */}
        <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${getStatusBadge(document.status)}`}>
          {document.status}
        </span>
      </div>
      
      {/* Metadata */}
      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
        {document.subcategory && (
          <span className="capitalize">{document.subcategory.replace(/_/g, ' ')}</span>
        )}
        {document.revisions && document.revisions.length > 0 && (
          <span>
            Updated {formatRelativeTime(document.revisions[document.revisions.length - 1].revisionDate)}
          </span>
        )}
      </div>
    </div>
  );
}

// Helper to format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString();
}

